// import { PlotTooltip, usePlotTooltip } from '@resistapp/client/components/tooltips/plot-tooltip';
import { ContextualTipId, useContextualTipContext } from '@resistapp/client/contexts/contextual-tip-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { GeneAndCopyNumber } from '@resistapp/client/data-utils/plot-data/process-overview-line-datum';
import { OverviewChartConfiguration } from '@resistapp/client/utils/overview-chart-configurations';
import { L2Targets } from '@resistapp/common/assays';
import { MetricMode } from '@resistapp/common/types';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridColumns } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Line } from '@visx/shape';
import { isNil } from 'lodash';
import { theme } from '../shared/theme';
import { CopyNumberBar } from './copy-number-bar';
import type { TargetType } from './site-details/treated-and-reduced-box/types';
import { TickLabel, useTickLabel } from './tick-label';
interface Props {
  genesAndNumbers: GeneAndCopyNumber[];
  width: number;
  height: number;
  events: boolean;
  metricMode: MetricMode;
  italic?: boolean;
  activeOverviewConfiguration: OverviewChartConfiguration;
  groupingTarget: TargetType;
}

export function CopyNumberBarGraph({
  width,
  height,
  genesAndNumbers,
  metricMode,
  activeOverviewConfiguration,
  italic = false,
  groupingTarget,
}: Props) {
  const { queryFilters } = useSampleDataContext();
  const { markInteraction } = useContextualTipContext();
  const { hoveredRowTarget, hoveredTarget, setHoveredTarget, onMouseEnter, onMouseLeave } = useTickLabel();
  // const tooltipStuff = useTooltipInPortal({
  //   scroll: false,
  //   detectBounds: true,
  // });
  // const { handleMouseMove, tooltipProps, tooltipData } = usePlotTooltip<GeneAndCopyNumber>(tooltipStuff);

  // TODO Remove reduction and 2 sided logic after we after we have confirmed that the new treated and removed bar graph is preferred for all cases
  if (metricMode === MetricMode.REDUCTION) {
    try {
      assertReductionData(genesAndNumbers, activeOverviewConfiguration);
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  const xMax = width - margins.horizontal;
  const yMax = height - margins.vertical;
  const xMinScale = metricMode === MetricMode.REDUCTION ? activeOverviewConfiguration.detailBarGraphMin : 1;
  const maxNumber = activeOverviewConfiguration.detailBarGraphMax(genesAndNumbers);
  const descendingGenesAndNumbers = [...genesAndNumbers].sort((a, b) => {
    const aValue = Number(a[activeOverviewConfiguration.scaleProperty]);
    const bValue = Number(b[activeOverviewConfiguration.scaleProperty]);
    if (isNil(aValue) || isNil(bValue)) {
      return 0;
    }
    return bValue - aValue;
  });
  const rangeXMax = xMax - twoSidedBarMargin.left * (twoSidedBarMargin.rightQuantifier + 1);

  // Capture the first gene name in the current graph (used for contextual tip anchoring)
  const firstGeneName = descendingGenesAndNumbers.length ? descendingGenesAndNumbers[0].gene : undefined;

  const yScale = scaleBand<string>({
    range: [0, yMax],
    round: true,
    domain: descendingGenesAndNumbers.map(d => getScaleLabel(d.assay, d.gene)),
    padding: 0.4,
  });
  const xScale = scaleLinear<number>({
    range: [0, rangeXMax],
    round: true,
    // The domain difference with is2Sided is a margin to the edge of graph to make the bars not end at the edge of the graph.
    domain: [xMinScale, maxNumber * 1.23],
  });

  // Custom tick values for the two-sided scale
  const tickValues = undefined;

  // We want overflow:visible, since if the values in the chart go beyond min or max it's better to show the values
  return (
    <svg width={width} height={height + yMarginForxAxisLegend} overflow="visible">
      <Group top={0} left={margins.horizontal} onMouseLeave={onMouseLeave}>
        <GridColumns
          left={0}
          height={height - 34}
          scale={xScale}
          stroke={theme.colors.neutral300}
          tickValues={tickValues}
          numTicks={6}
        />
        <Line from={{ x: 0, y: 0 }} to={{ x: 0, y: height - 34 }} stroke={theme.colors.neutral300} />
        <AxisBottom
          left={0}
          scale={xScale}
          top={yMax}
          tickFormat={n => activeOverviewConfiguration.formatNumber?.(n.valueOf())}
          numTicks={6}
          tickValues={tickValues}
          hideTicks
          hideAxisLine
          tickLabelProps={tickLabelProps}
        />
        {descendingGenesAndNumbers.map(geneAndCopyNumber => (
          <Group left={0} key={`bar-${geneAndCopyNumber.assay}`}>
            <CopyNumberBar
              key={`bar-${geneAndCopyNumber.assay}`}
              genesAndNumbers={descendingGenesAndNumbers}
              geneAndCopyNumber={geneAndCopyNumber}
              metricMode={metricMode}
              scaleProperty={activeOverviewConfiguration.scaleProperty}
              xScale={xScale}
              yScale={yScale}
              activeOverviewConfiguration={activeOverviewConfiguration}
              markInteraction={markInteraction}
              setHoveredTarget={setHoveredTarget}
            />
          </Group>
        ))}

        <AxisLeft
          scale={yScale}
          tickFormat={getGeneFromLabel}
          tickComponent={({ x, y, formattedValue }) => {
            if (!formattedValue) return null;

            const shortenedLabel = formattedValue.replace('resistance', 'res.');
            // Get the assay from the original label - we need to find which bar this corresponds to
            const assayLabel = yScale.domain()[yScale.domain().findIndex(d => d.includes(formattedValue))];
            const assay = assayLabel.split('#')[0];
            const isSelected =
              Boolean(assay) &&
              (queryFilters.filters.selectedTargets.length === 0 ||
                queryFilters.filters.selectedTargets.includes(assay as L2Targets));
            const isTheOnlySelected = isSelected && queryFilters.filters.selectedTargets.length === 1;

            const onClick = () => {
              if (assay) {
                queryFilters.toggleSingleTarget(assay);
                // Record interaction for contextual tip
                markInteraction(ContextualTipId.SITE_FOCUS_GENE_OR_ANTIBIOTIC);
              }
            };

            // Get the original identifier by finding which band contains this y position
            const originalIdentifier = yScale.domain().find((_, i) => {
              const bandStart = yScale(yScale.domain()[i]);
              const bandEnd = bandStart ? bandStart + yScale.bandwidth() : undefined;

              return bandStart !== undefined && bandEnd !== undefined && y >= bandStart && y <= bandEnd;
            });

            return metricMode === MetricMode.ARGI || metricMode === MetricMode.RISK ? (
              <TickLabel
                x={x}
                y={y}
                {...(formattedValue === firstGeneName ? { 'data-gene-focus-tip-anchor': 'true' } : {})}
                formattedValue={shortenedLabel}
                identifier={originalIdentifier as L2Targets}
                isSelected={isSelected}
                isTheOnlySelected={Boolean(isTheOnlySelected)}
                onMouseEnter={onMouseEnter}
                hoveredTarget={hoveredTarget}
                hoveredRowTarget={hoveredRowTarget}
                onClick={onClick}
                groupingTarget={groupingTarget}
                containerWidth={width}
              />
            ) : (
              <g {...(formattedValue === firstGeneName ? { 'data-gene-focus-tip-anchor': 'true' } : {})}>
                <text
                  x={x}
                  y={y}
                  fontSize={14}
                  fontWeight={isTheOnlySelected ? theme.fontWeight.extraHeavy : theme.fontWeight.bold}
                  fontStyle={italic ? 'italic' : 'normal'}
                  fill={theme.colors.neutral700}
                  dy=".33em"
                  textAnchor="end"
                  cursor="pointer"
                  onClick={onClick}
                >
                  {formattedValue}
                </text>
              </g>
            );
          }}
          hideTicks
          hideAxisLine
        />
      </Group>
    </svg>
  );
}

const margins = { vertical: 40, horizontal: 140 } as const;
const yMarginForxAxisLegend = 5 as const; // This is needed to prevent the bottom axis texts getting hidden
export const twoSidedBarMargin = {
  left: 10,
  rightQuantifier: 3,
} as const;
export const BarLabelThreshold = 50 as const;
export const tickLabelProps = {
  fontSize: 14,
  color: theme.colors.neutral700,
  fontWeight: theme.fontWeight.bold,
} as const;

export function getScaleLabel(assay: string, gene: string) {
  return `${assay}#${gene}`;
}

function getGeneFromLabel(label: string) {
  return label.split('#')[1];
}

function assertReductionData(
  genesAndCopyNumbers: GeneAndCopyNumber[],
  activeOverviewConfiguration: OverviewChartConfiguration,
): asserts genesAndCopyNumbers is Array<
  GeneAndCopyNumber & { [key in keyof typeof activeOverviewConfiguration.scaleProperty]: number }
> {
  if (genesAndCopyNumbers.some(d => d[activeOverviewConfiguration.scaleProperty] === undefined)) {
    throw new Error('All items must have reduction data when in reduction mode');
  }
}
